<template>
  <div>
    <v-row v-for="row in appInfoRows" :key="row.label" no-gutters class="py-2">
      <v-col class="text-body-small grey-darken-1--text">{{ row.label }}</v-col>
      <v-col class="text-body-small text-right">{{ row.value }}</v-col>
    </v-row>

    <div class="text-h2 mt-6 mb-4">{{ $t('profile.aboutApp.updates.title') }}</div>
    <div v-if="updateStatus === 'checking'" class="text-body-small grey-darken-1--text">
      {{ $t('profile.aboutApp.updates.checking') }}
    </div>
    <div v-else-if="updateStatus === 'up-to-date'" class="text-body-small grey-darken-1--text">
      {{ $t('profile.aboutApp.updates.upToDate') }}
    </div>
    <div v-else-if="updateStatus === 'error'" class="text-body-small error--text">
      {{ $t('profile.aboutApp.updates.error') }}
    </div>
    <template v-else-if="updateStatus === 'available'">
      <div class="text-body-small grey-darken-1--text mb-3">
        {{ $t('profile.aboutApp.updates.available') }}
      </div>
      <v-btn color="primary" block :loading="updating" @click="applyUpdate">
        {{ $t('profile.aboutApp.updates.apply') }}
      </v-btn>
    </template>
  </div>
</template>

<script>
import { App } from '@capacitor/app';
import { useCapacitorLiveUpdate } from '@/composables/capacitor/useCapacitorLiveUpdate.ts';

const liveUpdateConfig = () => ({
  url: `${import.meta.env.VITE_BFF_BASE_URL}/release/latest`,
  apiKey: import.meta.env.VITE_BFF_API_KEY
});

export default {
  name: 'AboutApp',
  data() {
    return {
      appInfoRows: [],
      updateStatus: 'checking', // 'checking' | 'available' | 'up-to-date' | 'error'
      pendingUpdate: null,
      updating: false
    };
  },
  computed: {
    isProduction() {
      return import.meta.env.MODE === 'production';
    }
  },
  async mounted() {
    const info = await App.getInfo();
    this.appInfoRows = [
      { label: this.$t('profile.aboutApp.appVersion'), value: info.version },
      { label: this.$t('profile.aboutApp.webVersion'), value: import.meta.env.VITE_APP_VERSION },
      { label: this.$t('profile.aboutApp.build'), value: info.build },
      { label: this.$t('profile.aboutApp.id'), value: info.id }
    ];

    await this.checkForUpdate();
  },
  methods: {
    async checkForUpdate() {
      this.updateStatus = 'checking';
      try {
        const { checkForUpdate } = useCapacitorLiveUpdate(liveUpdateConfig());
        const update = await checkForUpdate();
        if (update.available) {
          this.pendingUpdate = { bundleId: update.bundleId, url: update.url };
          this.updateStatus = 'available';
        } else {
          this.updateStatus = 'up-to-date';
        }
      } catch {
        this.updateStatus = 'error';
      }
    },
    async applyUpdate() {
      if (!this.pendingUpdate || this.updating) {
        return;
      }
      this.updating = true;
      try {
        const { activateBundle, reloadApp } = useCapacitorLiveUpdate(liveUpdateConfig());
        await activateBundle(this.pendingUpdate.bundleId, this.pendingUpdate.url);
        await reloadApp();
      } catch {
        this.updating = false;
        this.updateStatus = 'error';
      }
    }
  }
};
</script>
